import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, IconButton, Chip, Tooltip, CircularProgress, Alert, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Switch,
  Autocomplete, FormHelperText,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Route, Plus, RefreshCw, Search, Clock, Pencil } from "lucide-react";
import { useColorMode } from "../../../theme/ColorModeContext";
import axiosInstance from "../../../services/axiosInstance";
import { useTranslation } from "react-i18next";

const BRAND = "#7A3FE4";
const SCOPE_TONE = {
  global: { bg: "rgba(59,125,240,0.12)", fg: "#2563EB" },
  plan:   { bg: "rgba(232,147,12,0.14)", fg: "#B45309" },
  user:   { bg: "rgba(122,63,228,0.12)", fg: "#6D28D9" },
};
const EMPTY = { scope: "global", planId: "", userId: "", capability: "tts", realtimeVsPrompt: "both", provider: "", modelOrRoute: "", voiceId: "", effectiveFrom: "", notes: "" };

function fmtDate(d) { if (!d) return "—"; try { return new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); } catch { return String(d); } }

// A route row → dialog form (edit pre-fill). effectiveFrom is left empty so
// the replacement takes effect now unless the admin schedules it.
function rowToForm(r) {
  return {
    scope: r.scope, planId: r.planId || "", userId: r.userId != null ? String(r.userId) : "",
    capability: r.capability, realtimeVsPrompt: r.realtimeVsPrompt, provider: r.provider || "",
    modelOrRoute: r.modelOrRoute || "", voiceId: r.voiceId || "", effectiveFrom: "", notes: r.notes || "",
  };
}

function ProviderRoutingAdmin() {
  const { t }    = useTranslation("admin");
  const { mode } = useColorMode();
  const isLight  = mode === "light";

  const [rows, setRows]         = useState([]);
  const [options, setOptions]   = useState({ capabilities: [], modes: [], scopes: [] });
  const [targets, setTargets]   = useState({ plans: [], users: [], models: [], voices: [] });
  const [capFilter, setCapFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("");
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState("");
  const [toast, setToast]       = useState(null);

  // One dialog for add and edit; `editing` holds the row being replaced.
  const [dlgOpen, setDlgOpen]   = useState(false);
  const [editing, setEditing]   = useState(null);
  const [form, setForm]         = useState(EMPTY);
  const [saving, setSaving]     = useState(false);
  const [dlgError, setDlgError] = useState("");

  // Resolve panel
  const [rUser, setRUser] = useState("");
  const [rCap, setRCap]   = useState("tts");
  const [rMode, setRMode] = useState("realtime");
  const [resolved, setResolved] = useState(null);
  const [resolving, setResolving] = useState(false);

  const showToast = (msg, sev = "success") => { setToast({ msg, sev }); setTimeout(() => setToast(null), 6000); };

  const load = useCallback(() => {
    setLoading(true); setError("");
    const params = new URLSearchParams({ ...(capFilter ? { capability: capFilter } : {}), ...(scopeFilter ? { scope: scopeFilter } : {}) });
    axiosInstance.get(`/ai/admin/routing?${params}`)
      .then(r => { setRows(r.data.rows || []); setOptions(r.data.options || options); })
      .catch(e => setError(e.response?.data?.error || t("rtLoadFailed")))
      .finally(() => setLoading(false));
  }, [capFilter, scopeFilter, t]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadTargets = useCallback(() => {
    axiosInstance.get("/ai/admin/routing/targets")
      .then(r => setTargets({ plans: [], users: [], models: [], voices: [], ...r.data }))
      .catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTargets(); }, [loadTargets]);

  // Pickers derive from the rate card: a route can only target a priced model.
  const modelsForCap = useMemo(() => (targets.models || []).filter(m => m.capability === form.capability), [targets.models, form.capability]);
  const providersForCap = useMemo(() => [...new Set(modelsForCap.map(m => m.provider))], [modelsForCap]);
  const modelsForProvider = useMemo(() => modelsForCap.filter(m => m.provider === form.provider), [modelsForCap, form.provider]);
  const voiceById = useMemo(() => Object.fromEntries((targets.voices || []).map(v => [v.voiceId, v])), [targets.voices]);

  const openAdd = () => { setEditing(null); setForm(EMPTY); setDlgError(""); setDlgOpen(true); };
  const openEdit = (row) => { setEditing(row); setForm(rowToForm(row)); setDlgError(""); setDlgOpen(true); };

  const setCapability = (capability) => setForm(f => {
    const provs = [...new Set((targets.models || []).filter(m => m.capability === capability).map(m => m.provider))];
    return { ...f, capability, provider: provs.length === 1 ? provs[0] : (provs.includes(f.provider) ? f.provider : ""), modelOrRoute: "", voiceId: capability === "tts" ? f.voiceId : "" };
  });
  const setProvider = (provider) => setForm(f => ({ ...f, provider, modelOrRoute: "" }));

  const payload = () => ({
    ...form,
    planId: form.scope === "plan" ? form.planId : undefined,
    userId: form.scope === "user" ? Number(form.userId) : undefined,
    modelOrRoute: form.modelOrRoute || undefined,
    voiceId: form.voiceId || undefined,
    effectiveFrom: form.effectiveFrom || undefined,
  });

  const doSave = async () => {
    setSaving(true); setDlgError("");
    try {
      if (editing) await axiosInstance.put(`/ai/admin/routing/${editing.id}`, payload());
      else         await axiosInstance.post("/ai/admin/routing", payload());
      setDlgOpen(false); setForm(EMPTY); setEditing(null); load();
      showToast(editing ? t("rtEdited") : t("rtAdded"));
    } catch (e) { setDlgError(e.response?.data?.error || (editing ? t("rtEditFailed") : t("rtAddFailed"))); }
    finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      await axiosInstance.patch(`/ai/admin/routing/${row.id}`, { active: !row.active });
      setRows(rs => rs.map(r => r.id === row.id ? { ...r, active: !row.active ? 1 : 0 } : r));
    } catch (e) { showToast(e.response?.data?.error || t("rtToggleFailed"), "error"); }
  };

  const doResolve = async () => {
    setResolving(true); setResolved(null);
    try {
      const params = new URLSearchParams({ ...(rUser ? { userId: rUser } : {}), capability: rCap, mode: rMode });
      const { data } = await axiosInstance.get(`/ai/admin/routing/resolve?${params}`);
      setResolved(data);
    } catch (e) { setResolved({ error: e.response?.data?.error || t("rtResolveFailed") }); }
    finally { setResolving(false); }
  };

  const targetLabel = (r) => r.scope === "global" ? t("rtEveryone") : r.scope === "plan" ? (r.planName || r.planId) : (r.userEmail || r.userName || `#${r.userId}`);
  const headSx = { fontSize: 12, fontWeight: 700, color: "text.secondary", whiteSpace: "nowrap" };
  const cellSx = { fontSize: 13 };
  const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: "10px" } };
  const noModels = modelsForCap.length === 0;
  const canSave = !saving && !(noModels && !form.voiceId)
    && (!!form.modelOrRoute || !!form.voiceId)
    && (!form.modelOrRoute || !!form.provider)
    && (form.scope !== "plan" || !!form.planId) && (form.scope !== "user" || !!form.userId);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ width: 38, height: 38, borderRadius: "10px", bgcolor: alpha(BRAND, 0.12), display: "grid", placeItems: "center" }}>
          <Route size={20} color={BRAND} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700 }}>{t("rtTitle")}</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{t("rtSubtitle")}</Typography>
        </Box>
        <Button variant="contained" size="small" onClick={openAdd}
          startIcon={<Plus size={14} />} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600, boxShadow: "none" }}>
          {t("rtAddRoute")}
        </Button>
      </Box>

      {toast && <Alert severity={toast.sev} sx={{ mb: 2, borderRadius: "10px" }}>{toast.msg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "10px" }}>{error}</Alert>}

      {/* Resolve panel */}
      <Box sx={{ p: 2, mb: 2.5, border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper" }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1.25, display: "flex", alignItems: "center", gap: 0.75 }}><Search size={14} /> {t("rtResolveTitle")}</Typography>
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel>{t("rtTenant")}</InputLabel>
            <Select label={t("rtTenant")} value={rUser} onChange={e => setRUser(e.target.value)} sx={{ borderRadius: "10px" }} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
              <MenuItem value="">{t("rtNoTenant")}</MenuItem>
              {targets.users.map(u => <MenuItem key={u.id} value={String(u.id)}>#{u.id} · {u.email || u.name}{u.plan ? ` · ${u.plan}` : ""}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t("rtCapability")}</InputLabel>
            <Select label={t("rtCapability")} value={rCap} onChange={e => setRCap(e.target.value)} sx={{ borderRadius: "10px" }}>
              {(options.capabilities || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>{t("rtMode")}</InputLabel>
            <Select label={t("rtMode")} value={rMode} onChange={e => setRMode(e.target.value)} sx={{ borderRadius: "10px" }}>
              {["realtime", "prompt", "both"].map(m => <MenuItem key={m} value={m}>{t(`rtMode_${m}`)}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" size="small" onClick={doResolve} disabled={resolving} sx={{ textTransform: "none", borderRadius: "10px", fontWeight: 600 }}>
            {resolving ? <CircularProgress size={13} /> : t("rtResolve")}
          </Button>
          {resolved && (
            <Box sx={{ flexBasis: "100%", mt: 1, px: 1.5, py: 1, borderRadius: "10px", bgcolor: alpha(BRAND, 0.06), fontFamily: "var(--font-mono)", fontSize: 12.5 }}>
              {resolved.error ? <span style={{ color: "#DC2626" }}>{resolved.error}</span>
                : !resolved.resolved ? <span>{t("rtResolvedNone")}</span>
                : <span>
                    <b>{resolved.resolved.provider ?? "—"}</b> / <b>{resolved.resolved.modelOrRoute ?? t("rtInherit")}</b>
                    {" "}<Chip size="small" label={resolved.resolved.scope ?? "—"} sx={{ height: 18, fontSize: 10, ml: 0.5 }} />
                    {" · "}{t("rtVoice")}: <b>{resolved.resolved.voiceId ?? "—"}</b>
                    {resolved.resolved.voiceId && voiceById[resolved.resolved.voiceId] ? <span style={{ opacity: 0.7 }}> ({voiceById[resolved.resolved.voiceId].name})</span> : null}
                    {resolved.resolved.voiceScope ? <Chip size="small" label={resolved.resolved.voiceScope} sx={{ height: 18, fontSize: 10, ml: 0.5 }} /> : null}
                    {" · row #"}{resolved.resolved.routeId ?? "—"}
                  </span>}
            </Box>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("rtCapability")}</InputLabel>
          <Select label={t("rtCapability")} value={capFilter} onChange={e => setCapFilter(e.target.value)} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">{t("rtAll")}</MenuItem>
            {(options.capabilities || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>{t("rtScope")}</InputLabel>
          <Select label={t("rtScope")} value={scopeFilter} onChange={e => setScopeFilter(e.target.value)} sx={{ borderRadius: "10px" }}>
            <MenuItem value="">{t("rtAll")}</MenuItem>
            {(options.scopes || []).map(s => <MenuItem key={s} value={s}>{t(`rtScope_${s}`)}</MenuItem>)}
          </Select>
        </FormControl>
        <Tooltip title={t("refresh")}><IconButton size="small" onClick={() => { load(); loadTargets(); }}><RefreshCw size={15} /></IconButton></Tooltip>
      </Box>

      {/* Table */}
      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: "12px", bgcolor: isLight ? "#fff" : "background.paper" }}>
        <Table size="small">
          <TableHead><TableRow>
            <TableCell sx={headSx}>{t("rtCapability")}</TableCell>
            <TableCell sx={headSx}>{t("rtScope")}</TableCell>
            <TableCell sx={headSx}>{t("rtTarget")}</TableCell>
            <TableCell sx={headSx}>{t("rtMode")}</TableCell>
            <TableCell sx={headSx}>{t("rtProvider")}</TableCell>
            <TableCell sx={headSx}>{t("rtModel")}</TableCell>
            <TableCell sx={headSx}>{t("rtVoice")}</TableCell>
            <TableCell sx={headSx}>{t("rtEffective")}</TableCell>
            <TableCell sx={headSx} align="center">{t("rtActive")}</TableCell>
            <TableCell sx={headSx} align="right">{t("rtActions")}</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 5 }}><CircularProgress size={22} /></TableCell></TableRow>}
            {!loading && rows.length === 0 && <TableRow><TableCell colSpan={10} align="center" sx={{ py: 6, color: "text.secondary", fontSize: 13 }}>{t("rtEmpty")}</TableCell></TableRow>}
            {!loading && rows.map(r => {
              const tone = SCOPE_TONE[r.scope] || SCOPE_TONE.global;
              const voice = r.voiceId ? voiceById[r.voiceId] : null;
              return (
                <TableRow key={r.id} hover sx={!r.active ? { opacity: 0.5 } : r.isFuture ? { opacity: 0.8 } : undefined}>
                  <TableCell sx={cellSx}>{r.capability}</TableCell>
                  <TableCell><Chip size="small" label={t(`rtScope_${r.scope}`)} sx={{ height: 20, fontSize: 11, fontWeight: 700, bgcolor: tone.bg, color: tone.fg }} /></TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{targetLabel(r)}</TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>{t(`rtMode_${r.realtimeVsPrompt}`)}</TableCell>
                  <TableCell sx={cellSx}>{r.provider}</TableCell>
                  <TableCell sx={{ ...cellSx, fontFamily: "var(--font-mono)", fontSize: 12 }}>{r.modelOrRoute || <em style={{ opacity: 0.6 }}>{t("rtInherit")}</em>}</TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12 }}>
                    {voice ? <Tooltip title={r.voiceId}><span>{voice.name}</span></Tooltip>
                      : <span style={{ fontFamily: "var(--font-mono)", fontSize: 11.5 }}>{r.voiceId || "—"}</span>}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontSize: 12, whiteSpace: "nowrap" }}>
                    {fmtDate(r.effectiveFrom)}
                    {r.isFuture ? <Chip size="small" icon={<Clock size={10} />} label={t("rcScheduled")} sx={{ ml: 0.75, height: 18, fontSize: 10, bgcolor: alpha("#E8930C", 0.12), color: "#B45309", "& .MuiChip-icon": { color: "inherit" } }} /> : null}
                  </TableCell>
                  <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                    <Switch size="small" checked={!!r.active} onChange={() => toggleActive(r)} />
                    {r.supersededById ? <Chip size="small" label={`${t("rtReplacedBy")} #${r.supersededById}`} sx={{ ml: 0.5, height: 18, fontSize: 10 }} /> : null}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t("rtEdit")}><IconButton size="small" onClick={() => openEdit(r)}><Pencil size={14} /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add / edit dialog */}
      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px" } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 16 }}>
          {editing ? `${t("rtEditTitle")} · #${editing.id}` : t("rtAddTitle")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 2 }}>{editing ? t("rtEditDesc") : t("rtAddDesc")}</Typography>
          {dlgError && <Alert severity="error" sx={{ mb: 2 }}>{dlgError}</Alert>}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
            <FormControl size="small"><InputLabel>{t("rtScope")}</InputLabel>
              <Select label={t("rtScope")} value={form.scope} onChange={e => setForm(f => ({ ...f, scope: e.target.value }))}>
                {["global", "plan", "user"].map(s => <MenuItem key={s} value={s}>{t(`rtScope_${s}`)}</MenuItem>)}
              </Select>
            </FormControl>
            {form.scope === "plan" && (
              <FormControl size="small"><InputLabel>{t("rtPlan")}</InputLabel>
                <Select label={t("rtPlan")} value={form.planId} onChange={e => setForm(f => ({ ...f, planId: e.target.value }))}>
                  {targets.plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name} ({p.id})</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {form.scope === "user" && (
              <FormControl size="small"><InputLabel>{t("rtTenant")}</InputLabel>
                <Select label={t("rtTenant")} value={form.userId} onChange={e => setForm(f => ({ ...f, userId: e.target.value }))} MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
                  {targets.users.map(u => <MenuItem key={u.id} value={String(u.id)}>#{u.id} · {u.email || u.name}</MenuItem>)}
                </Select>
              </FormControl>
            )}
            {form.scope === "global" && <Box />}

            <FormControl size="small"><InputLabel>{t("rtCapability")}</InputLabel>
              <Select label={t("rtCapability")} value={form.capability} onChange={e => setCapability(e.target.value)}>
                {(options.capabilities || []).map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small"><InputLabel>{t("rtMode")}</InputLabel>
              <Select label={t("rtMode")} value={form.realtimeVsPrompt} onChange={e => setForm(f => ({ ...f, realtimeVsPrompt: e.target.value }))}>
                {["realtime", "prompt", "both"].map(m => <MenuItem key={m} value={m}>{t(`rtMode_${m}`)}</MenuItem>)}
              </Select>
            </FormControl>

            {noModels && (
              <Alert severity="warning" sx={{ gridColumn: { sm: "1 / -1" }, borderRadius: "10px", py: 0.5 }}>
                <b>{t("rtNoModels")}</b> {t("rtNoModelsHelp")}
              </Alert>
            )}
            <FormControl size="small" disabled={noModels}><InputLabel>{t("rtProvider")}</InputLabel>
              <Select label={t("rtProvider")} value={providersForCap.includes(form.provider) ? form.provider : ""} onChange={e => setProvider(e.target.value)}>
                {providersForCap.map(p => <MenuItem key={p} value={p}>{p}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl size="small" disabled={noModels || !form.provider}><InputLabel>{t("rtModel")}</InputLabel>
              <Select label={t("rtModel")} value={modelsForProvider.some(m => m.modelOrRoute === form.modelOrRoute) ? form.modelOrRoute : ""}
                onChange={e => setForm(f => ({ ...f, modelOrRoute: e.target.value }))}>
                {form.capability === "tts" && <MenuItem value=""><em>{t("rtInheritModel")}</em></MenuItem>}
                {modelsForProvider.map(m => <MenuItem key={m.modelOrRoute} value={m.modelOrRoute} sx={{ fontFamily: "var(--font-mono)", fontSize: 13 }}>{m.modelOrRoute}</MenuItem>)}
              </Select>
              <FormHelperText>{form.capability === "tts" ? t("rtModelHelpTts") : t("rtModelHelp")}</FormHelperText>
            </FormControl>

            {form.capability === "tts" && (
              <Autocomplete
                freeSolo size="small" sx={{ gridColumn: { sm: "1 / -1" } }}
                options={targets.voices || []}
                value={form.voiceId ? (voiceById[form.voiceId] || form.voiceId) : null}
                inputValue={form.voiceId}
                onInputChange={(_e, v, reason) => { if (reason === "input" || reason === "clear") setForm(f => ({ ...f, voiceId: v || "" })); }}
                onChange={(_e, v) => setForm(f => ({ ...f, voiceId: typeof v === "string" ? v : (v?.voiceId || "") }))}
                getOptionLabel={o => typeof o === "string" ? o : o.voiceId}
                isOptionEqualToValue={(o, v) => (typeof v === "string" ? o.voiceId === v : o.voiceId === v?.voiceId)}
                filterOptions={(opts, { inputValue }) => {
                  const q = inputValue.trim().toLowerCase();
                  return q ? opts.filter(o => o.voiceId.toLowerCase().includes(q) || o.name.toLowerCase().includes(q) || o.dialect.toLowerCase().includes(q)) : opts;
                }}
                renderOption={(props, o) => (
                  <li {...props} key={o.voiceId}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: 13 }}>{o.name}</span>
                      <span style={{ fontSize: 11, opacity: 0.65, fontFamily: "var(--font-mono)" }}>{o.dialect} · {o.gender} · {o.voiceId}</span>
                    </Box>
                  </li>
                )}
                renderInput={params => (
                  <TextField {...params} label={t("rtVoice")} sx={inputSx}
                    helperText={form.voiceId && voiceById[form.voiceId] ? `${voiceById[form.voiceId].name} · ${voiceById[form.voiceId].dialect} · ${voiceById[form.voiceId].gender}` : t("rtVoicePick")} />
                )}
              />
            )}

            <TextField size="small" label={t("rcEffectiveFrom")} type="datetime-local" InputLabelProps={{ shrink: true }} value={form.effectiveFrom}
              onChange={e => setForm(f => ({ ...f, effectiveFrom: e.target.value }))} helperText={t("rcEffectiveHelp")} sx={inputSx} />
            <TextField size="small" label={t("rcNotes")} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} sx={inputSx} inputProps={{ maxLength: 255 }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: "none" }}>{t("cancel")}</Button>
          <Button variant="contained" onClick={doSave} disabled={!canSave}
            sx={{ textTransform: "none", fontWeight: 600, boxShadow: "none", borderRadius: "10px" }}>
            {saving ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : t("rtSave")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ProviderRoutingAdmin;
